import { WorkflowId } from "@sp-crm/core";
import { QueryRenderer } from "components/clients/show-client/community-comparison/query-renderer";
import { Spinner } from "components/ui/spinner";
import {
    AdvancedSearchEntityType,
    CustomListKey,
    useCreateWorkflowMutation,
    useGetWorkflowsQuery,
    WorkflowStatus,
} from "generated/graphql";
import React from "react";
import { Redirect, useRouteMatch } from "react-router-dom";
import { navigate } from "store/actions";
import {
    useBridgeTypes,
    useCustomList,
    useWorkflowEntityTypes,
} from "store/selectors/bridge";
import { useCurrentUserId, useDefaultRegionVisibility } from "store/selectors/hooks";
import { handleEvent } from "util/user-events";
import { workflowEventProperties } from "./builder-analytics";
import { workflowsForEntityTypes } from "./builder-helper";
import {
    BlueprintContext,
    composeNewWorkflow,
    defaultBlueprint,
    generateActionId,
} from "./workflow-blueprint";
import { WorkflowCardList } from "./workflow-card-list";
import { WorkflowDetailsPage } from "./workflow-details-page";
import { WorkflowWizard } from "./workflow-wizard";

const EDIT_ROUTE = "/settings/workflow/:workflowId/edit";

export const WorkflowBuilderPage: React.FC = () => {
    const { params } = useRouteMatch<{ workflowId?: WorkflowId }>();
    const editing = !!useRouteMatch(EDIT_ROUTE);
    const workflows = useGetWorkflowsQuery();
    const workflowEntityTypes = useWorkflowEntityTypes();
    const { status: bridgeTypesStatus } = useBridgeTypes();
    const currentUserId = useCurrentUserId();
    const defaultRegionVisibility = useDefaultRegionVisibility();
    const createWorkflowMutation = useCreateWorkflowMutation();
    const { customList: clientStatusCustomList } = useCustomList(
        CustomListKey.ClientStatus,
    );
    const { customList: homeCareLeadStatusCustomList } = useCustomList(
        CustomListKey.HomeCareLeadStatus,
    );
    const [searchText, setSearchText] = React.useState("");
    const [entityTypeFilter, setEntityTypeFilter] = React.useState<
        AdvancedSearchEntityType | "all"
    >("all");
    const [statusFilter, setStatusFilter] = React.useState<WorkflowStatus | "all">("all");

    const handleRefetch = React.useCallback(async () => {
        await workflows.refetch();
    }, [workflows]);

    const blueprintContext = React.useMemo<BlueprintContext>(
        () => ({
            clientStatusCustomList: clientStatusCustomList ?? null,
            homeCareLeadStatusCustomList: homeCareLeadStatusCustomList ?? null,
            newActionId: generateActionId,
        }),
        [clientStatusCustomList, homeCareLeadStatusCustomList],
    );

    const createBlueprint = React.useMemo(
        () => defaultBlueprint(workflowEntityTypes, blueprintContext),
        [workflowEntityTypes, blueprintContext],
    );

    const visibleWorkflows = React.useMemo(
        () =>
            workflowsForEntityTypes(
                workflowEntityTypes,
                workflows.data?.getWorkflows ?? [],
            ),
        [workflowEntityTypes, workflows.data?.getWorkflows],
    );

    const createInFlight = React.useRef(false);

    const handleCreate = React.useCallback(async () => {
        if (createInFlight.current || !createBlueprint) {
            return;
        }
        const existingNames = (workflows.data?.getWorkflows ?? []).map(
            workflow => workflow.name,
        );
        const payload = composeNewWorkflow(createBlueprint, blueprintContext, {
            currentUserId,
            regionVisibility: defaultRegionVisibility,
            existingNames,
        });
        if (!payload) {
            return;
        }
        createInFlight.current = true;
        try {
            const result = await createWorkflowMutation.mutateAsync({
                workflow: payload,
            });
            handleEvent("workflow-builder-create", {
                ...workflowEventProperties(
                    {
                        id: result.createWorkflow.id,
                        attachTo: createBlueprint.entityType,
                        status: WorkflowStatus.Draft,
                    },
                    createBlueprint,
                ),
                existingWorkflowCount: visibleWorkflows.length,
            });
            await workflows.refetch();
            navigate(`/settings/workflow/${result.createWorkflow.id}/edit`);
        } finally {
            createInFlight.current = false;
        }
    }, [
        createBlueprint,
        blueprintContext,
        currentUserId,
        defaultRegionVisibility,
        createWorkflowMutation,
        workflows,
        visibleWorkflows,
    ]);

    const canCreate = !!createBlueprint && !createWorkflowMutation.isLoading;

    return (
        <QueryRenderer name="WorkflowBuilderPage" query={workflows}>
            {data => {
                const selectedWorkflow = params?.workflowId
                    ? visibleWorkflows.find(w => w.id === params.workflowId) || null
                    : null;

                if (params?.workflowId && !selectedWorkflow) {
                    return bridgeTypesStatus === "ready" ? (
                        <Redirect to="/settings/workflow" />
                    ) : (
                        <Spinner />
                    );
                }

                if (selectedWorkflow && editing) {
                    return (
                        <WorkflowWizard
                            key={selectedWorkflow.id}
                            workflow={selectedWorkflow}
                            workflowSettings={data.getWorkflowSettings}
                            refetch={handleRefetch}
                        />
                    );
                }

                if (selectedWorkflow) {
                    return (
                        <WorkflowDetailsPage
                            key={selectedWorkflow.id}
                            workflow={selectedWorkflow}
                            workflowSettings={data.getWorkflowSettings}
                            refetch={handleRefetch}
                        />
                    );
                }

                return (
                    <WorkflowCardList
                        workflows={visibleWorkflows}
                        refreshing={workflows.isFetching}
                        onCreate={handleCreate}
                        createEnabled={canCreate}
                        searchText={searchText}
                        onSearchTextChange={setSearchText}
                        entityTypeFilter={entityTypeFilter}
                        onEntityTypeFilterChange={setEntityTypeFilter}
                        statusFilter={statusFilter}
                        onStatusFilterChange={setStatusFilter}
                    />
                );
            }}
        </QueryRenderer>
    );
};
